import cn from 'classnames';
import type { Log } from '../../worker/log-worker-types.ts';
import styles from './DebugLine.module.css';

type Props = {
  log: Log | string;
  variant?: 'log' | 'command';
};

/**
 * Comparator function to organize a single log message a bit, to show timestamp, then log level, then message, then rest
 */
export function sortLog(a: [string, unknown], b: [string, unknown]): number {
  const [aKey] = a;
  const [bKey] = b;

  if (aKey === 'time') return -1;
  if (bKey === 'time') return 1;

  if (aKey === 'level') return -1;
  if (bKey === 'level') return 1;

  if (aKey === 'message' || aKey === 'msg') return -1;
  if (bKey === 'message' || bKey === 'msg') return 1;

  return 0;
}

export function DebugLine({ log, variant = 'log' }: Props) {
  // If we didn't get a valid log, show the malformed text with some margin around it.
  if (typeof log === 'string') {
    return <div className="mb-24 mt-24">{log}</div>;
  }

  return (
    <div>
      {Object.entries(log)
        .toSorted(sortLog)
        .map(([key, val]) => {
          const text = typeof val === 'string' ? val : JSON.stringify(val, null, 2);
          // Pretty-printed JSON renders as a block below its key; a block around a
          // single-line value would push it off the `key=` line instead.
          const Element = variant === 'command' && text.includes('\n') ? 'div' : 'span';
          return (
            <span key={key} className={cn(styles.DebugLine, { [styles.DebugLine___command]: variant === 'command' })}>
              <span className={styles[`DebugLine_key___${variant}`]}>{key}</span>=
              <Element
                className={cn(styles.DebugLine_value, {
                  [styles.DebugLine_value___error]: key === 'level' && val === 'error',
                  [styles.DebugLine_value___wrap]: variant === 'command',
                })}
              >
                {text}{' '}
              </Element>
            </span>
          );
        })}
    </div>
  );
}
